import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuestionsFeed, useQuestionDetail, useSubmitAttempt, useEnrollments, useUpdateSession, useSession, useExamSectionLookup, useCompleteSession, useQuestionComments, usePostQuestionComment, useDeleteQuestionComment, useVoteComment, useReportComment, useCreateShare, useReportQuestion, useMe } from '../lib/api';
import type { QuestionComment } from '../lib/api';
import { useXp } from '../lib/xp-context';
import type { ApiQuestion } from '@shared/types';
import { deriveCategories } from '../lib/topic-tags';
import { addFahrenheit } from '../lib/temperature';
import { getFlaggedQuestions, toggleFlaggedQuestion, isFlagged } from '../lib/flagged-questions';
import { recordAttempt, flushQueue, newAttemptId } from '../lib/attempt-queue';
import { SwipeCard, SwipeTutorialOverlay, useSwipeTutorial } from '../components/SwipeCard';
import { scrollAppToTop } from '../lib/scroll';
import { motion, AnimatePresence, springs, Pressable, FadeIn, Stagger, CountUp, Confetti, Skeleton } from '../lib/motion';
import { haptic } from '../lib/haptics';
import { holdRefresh, releaseRefresh } from '../lib/appRefresh';

/**
 * Restore intra-case order for passage-based question sets.
 *
 * The questions feed sorts by created_at DESC, which hands back a passage's
 * questions in reverse insertion order. For sets whose questions are sequential
 * (COMLEX Day 2 clinical decision-making cases progress diagnosis -> next step
 * -> escalation -> follow-up) that reversal shows the last decision first.
 *
 * Each run of consecutive questions sharing one passage is re-sorted by the
 * `case_seq` the generator stamped on them. Runs where any question lacks a
 * numeric case_seq are left exactly as-is, so MCAT passages are unaffected.
 * Sorting only WITHIN a run keeps runs contiguous, which is what the passage
 * grouping and highlighting depend on.
 */
function orderPassageCases<T extends { payload: any }>(qs: T[]): T[] {
  const out = [...qs];
  for (let i = 0; i < out.length;) {
    const passage = out[i]!.payload?.passage;
    if (!passage) { i++; continue; }
    let j = i;
    while (j < out.length && out[j]!.payload?.passage === passage) j++;
    const run = out.slice(i, j);
    if (run.length > 1 && run.every((q) => typeof q.payload?.case_seq === 'number')) {
      run.sort((a, b) => a.payload.case_seq - b.payload.case_seq);
      out.splice(i, run.length, ...run);
    }
    i = j;
  }
  return out;
}

export function StudyPage() {
  const { contextType, contextId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');

  // Never silently hard-refresh out from under an active study session. A held
  // instant update waits until the session ends (or the page unmounts), so a
  // deploy can't wipe someone's place mid session. Banner updates are unaffected
  // (they only reload when the user taps Reload).
  useEffect(() => {
    holdRefresh('study-session');
    return () => releaseRefresh('study-session');
  }, []);

  // Look up parent exam so we can fetch ALL sections' questions when in a session
  const examSectionId = contextType === 'exam' ? contextId : null;
  const { data: sectionLookupData } = useExamSectionLookup(examSectionId ?? null);
  const parentExamId = sectionLookupData?.exam?.id ?? null;

  // When we have a session in exam context, fetch by exam_id (all sections) so all
  // session question IDs can be resolved — sessions may span multiple sections.
  const params = contextType === 'class'
    ? { class_section_id: contextId! }
    : (sessionId && parentExamId)
      ? { exam_id: parentExamId }
      : { exam_section_id: contextId! };
  const { data, isLoading } = useQuestionsFeed(params);
  const { data: enrollData } = useEnrollments();
  const { data: sessionData } = useSession(sessionId);
  const updateSession = useUpdateSession();
  const completeSession = useCompleteSession();
  const navigate = useNavigate();
  const xp = useXp();
  const queueFlushSubmit = useSubmitAttempt();

  // Retry any attempts a previous session could not save: on entering the study
  // screen, and whenever the app comes back to the foreground (the iOS WebView
  // suspends background fetches, so this is where stranded attempts get sent).
  useEffect(() => {
    const flush = () => { void flushQueue(queueFlushSubmit.mutateAsync); };
    flush();
    const onVisible = () => { if (document.visibilityState === 'visible') flush(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', flush);
    };
    // mutateAsync is stable for the lifetime of the mutation object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [idx, setIdx] = useState(0);
  const [sessionComplete, setSessionComplete] = useState(false);
  // Visual-only: consecutive correct answers this sitting (drives the streak chip)
  const [correctStreak, setCorrectStreak] = useState(0);
  const hydratedSessionRef = useRef<string | null>(null);
  const hydratedSessionIdRef = useRef<string | null>(null);

  // Sessionless resume: without a study session, position only lives in this
  // component, so a reload (deploy refresh, crash, force-close) restarted at
  // question 1. Persist the index per context in localStorage and restore it on
  // mount so sessionless studying survives reloads. Sessions hydrate their own
  // index from the server and ignore this.
  const posKey = `study-pos:${contextType}:${contextId}`;
  const restoredPosRef = useRef(false);
  useEffect(() => {
    if (sessionId || restoredPosRef.current) return;
    restoredPosRef.current = true;
    try {
      const raw = localStorage.getItem(posKey);
      if (!raw) return;
      const saved = JSON.parse(raw) as { i: number; t: number };
      // Honor for 48h; ignore garbage.
      if (typeof saved.i === 'number' && saved.i > 0 && Date.now() - saved.t < 48 * 3600_000) {
        setIdx(saved.i);
      }
    } catch { /* ignore */ }
  }, [sessionId, posKey]);
  useEffect(() => {
    if (sessionId) return; // sessions persist server-side
    try {
      if (idx > 0) localStorage.setItem(posKey, JSON.stringify({ i: idx, t: Date.now() }));
      else localStorage.removeItem(posKey);
    } catch { /* ignore */ }
  }, [idx, sessionId, posKey]);
  // Clear the stored position once the run is completed.
  useEffect(() => {
    if (!sessionId && sessionComplete) { try { localStorage.removeItem(posKey); } catch { /* ignore */ } }
  }, [sessionComplete, sessionId, posKey]);

  // Track per-question results for session complete screen
  const sessionResultsRef = useRef<Record<string, { correct: boolean; difficulty: number; topic: string }>>({});
  const filteredQuestionsRef = useRef<ApiQuestion[]>([]);

  // Track per-question answered state so going back shows previous answers
  const [answeredState, setAnsweredState] = useState<Record<string, { selected: number[]; revealed: boolean; locked?: boolean; result: any }>>({});

  // Session state — use refs so callbacks always see current values
  const [sessionQuestionIds, setSessionQuestionIds] = useState<string[] | null>(null);
  const [sessionCorrectCount, setSessionCorrectCount] = useState(0);
  const [sessionAnswered, setSessionAnswered] = useState(0);
  const [sessionXpEarned, setSessionXpEarned] = useState(0);
  const sessionAnsweredRef = useRef(0);
  const sessionCorrectRef = useRef(0);
  const sessionXpRef = useRef(0);

  const enrollment = contextType === 'class'
    ? enrollData?.enrollments?.find((e: any) => e.id === contextId)
    : null;

  // Back navigation uses the same section lookup from above
  const backPath = contextType === 'class'
    ? `/class/${contextId}`
    : sectionLookupData?.exam ? `/exam/${sectionLookupData.exam.id}` : '/exams';

  // Initialize session state from server when resuming
  useEffect(() => {
    if (!sessionData?.session) return;
    const s = sessionData.session;
    const answersKey = JSON.stringify(s.answers ?? {});
    const hydrationKey = `${s.id}:${s.currentIndex}:${s.questionsAnswered}:${s.correctCount}:${s.xpEarned}:${answersKey}`;
    if (hydratedSessionRef.current === hydrationKey) return;
    hydratedSessionRef.current = hydrationKey;

    setSessionQuestionIds(s.questionIds);
    setSessionCorrectCount(s.correctCount);
    setSessionAnswered(s.questionsAnswered);
    setSessionXpEarned(s.xpEarned);
    sessionAnsweredRef.current = s.questionsAnswered;
    sessionCorrectRef.current = s.correctCount;
    sessionXpRef.current = s.xpEarned;
    const isSameSession = hydratedSessionIdRef.current === s.id;
    hydratedSessionIdRef.current = s.id;
    setAnsweredState((prev) => {
      const hydratedAnswers = s.answers
        ? Object.fromEntries(
        Object.entries(s.answers).map(([questionId, answer]) => [
          questionId,
          {
            selected: answer.selectedAnswers ?? [],
            revealed: true,
            result: {
              xpEarned: answer.correct ? 15 : -5,
              questionStats: undefined,
            },
          },
        ]),
      )
        : {};
      return isSameSession ? { ...hydratedAnswers, ...prev } : hydratedAnswers;
    });
    if (!isSameSession) setIdx(s.currentIndex);
  }, [sessionData]);

  useEffect(() => {
    scrollAppToTop('smooth');
  }, [idx]);

  /**
   * Called TWICE per answer: once locally the instant the user taps ("local"),
   * and again if and when the server records the attempt ("server").
   *
   * Counting is driven by `result.correct`, which is computed from the question
   * payload, NOT by `xpEarned > 0`. Those are not the same thing: a correct
   * answer can legitimately earn 0 XP (negative-XP clamping), and an answer the
   * server never recorded has no XP at all. Inferring correctness from XP made a
   * correct answer count as wrong on the results screen and reset the streak.
   */
  const handleAttemptResult = useCallback((result: any, questionId?: string, phase: 'local' | 'server' = 'server') => {
    if (!result) return;

    // XP is only ever known by the server, so it is applied when the response
    // lands. Everything else is settled locally so the UI never waits.
    if (phase === 'server') {
      if (typeof result.totalXp === 'number') xp.setTotalXp(result.totalXp);
      if (typeof result.contextXp === 'number') xp.setContextXp(result.contextXp);
      const totalDelta = (result.xpEarned ?? 0) + (result.speedBonus ?? 0);
      if (totalDelta !== 0) xp.pushDelta(totalDelta);
      if (sessionId && totalDelta !== 0) {
        sessionXpRef.current += totalDelta;
        setSessionXpEarned(sessionXpRef.current);
        updateSession.mutate({
          id: sessionId,
          questionsAnswered: sessionAnsweredRef.current,
          correctCount: sessionCorrectRef.current,
          xpEarned: sessionXpRef.current,
        });
      }
      return;
    }

    setCorrectStreak((s) => (result.correct ? s + 1 : 0));

    // Track per-question result for session complete screen
    if (questionId) {
      const q = filteredQuestionsRef.current.find(fq => fq.id === questionId);
      if (q) {
        sessionResultsRef.current[questionId] = {
          correct: Boolean(result.correct),
          difficulty: (q as any).difficulty ?? 3,
          topic: (q.payload as any)?.topic ?? 'Unknown',
        };
      }
    }

    // Track session progress using refs for accurate counts
    if (sessionId) {
      sessionAnsweredRef.current += 1;
      sessionCorrectRef.current += result.correct ? 1 : 0;
      setSessionAnswered(sessionAnsweredRef.current);
      setSessionCorrectCount(sessionCorrectRef.current);
      // Persist immediately so progress isn't lost if user leaves
      updateSession.mutate({
        id: sessionId,
        questionsAnswered: sessionAnsweredRef.current,
        correctCount: sessionCorrectRef.current,
        xpEarned: sessionXpRef.current,
      });
    }
  }, [xp, sessionId, updateSession]);

  // Update session on the server when navigating to next question
  const updateSessionProgress = useCallback((newIndex: number) => {
    if (!sessionId) return;
    updateSession.mutate({
      id: sessionId,
      currentIndex: newIndex,
      questionsAnswered: sessionAnsweredRef.current,
      correctCount: sessionCorrectRef.current,
      xpEarned: sessionXpRef.current,
    });
  }, [sessionId, updateSession]);

  // Filter by selected topics from URL params
  const topicFilter = searchParams.get('topics');
  const selectedTopics = useMemo(
    () => topicFilter ? new Set(topicFilter.split('||')) : null,
    [topicFilter],
  );

  const filteredQuestions = useMemo(() => {
    if (!data?.questions.length) return [];
    let qs = data.questions.filter((q) => q.type !== 'flashcard' && q.type !== 'quick_mcq');

    // If we have a session, filter to only those question IDs (preserving order)
    if (sessionQuestionIds) {
      const idSet = new Set(sessionQuestionIds);
      const byId = new Map(qs.map(q => [q.id, q]));
      qs = sessionQuestionIds.map(id => byId.get(id)).filter(Boolean) as typeof qs;
    } else if (selectedTopics) {
      qs = qs.filter((q) => {
        const cats = deriveCategories(q.payload as any);
        return cats.some((c) => selectedTopics.has(c));
      });
    }
    return orderPassageCases(qs);
  }, [data?.questions, selectedTopics, sessionQuestionIds]);
  filteredQuestionsRef.current = filteredQuestions;

  // Passage review mode: 'immediate' shows answers right away, 'end' defers to end of passage
  const [passageReviewMode, setPassageReviewMode] = useState<'immediate' | 'end'>('immediate');
  // Deferred answers: questionId -> { selected, correct, result }
  const [deferredAnswers, setDeferredAnswers] = useState<Record<string, { selected: number[]; correct: boolean; result: any }>>({});
  // Whether we're showing the passage review screen
  const [showingPassageReview, setShowingPassageReview] = useState(false);

  // Identify passage groups: consecutive questions sharing the same passage text
  const passageGroups = useMemo(() => {
    const groups: { startIdx: number; endIdx: number; passage: string }[] = [];
    let i = 0;
    while (i < filteredQuestions.length) {
      const p = (filteredQuestions[i]!.payload as any)?.passage;
      if (p) {
        const start = i;
        while (i < filteredQuestions.length && (filteredQuestions[i]!.payload as any)?.passage === p) i++;
        groups.push({ startIdx: start, endIdx: i - 1, passage: p });
      } else {
        i++;
      }
    }
    return groups;
  }, [filteredQuestions]);

  // Handle deferred answer storage
  const handleDeferredAnswer = useCallback((questionId: string, selected: number[], correct: boolean, result: any) => {
    setDeferredAnswers(prev => ({ ...prev, [questionId]: { selected, correct, result } }));
  }, []);

  // Passage review: collect all questions and answers for this passage group
  const currentPassageGroup = passageGroups.find(g => idx >= g.startIdx && idx <= g.endIdx);
  const passageReviewQuestions = useMemo(() => {
    if (!currentPassageGroup) return [];
    return filteredQuestions.slice(currentPassageGroup.startIdx, currentPassageGroup.endIdx + 1);
  }, [currentPassageGroup, filteredQuestions]);

  const handleExitReview = useCallback(() => {
    setShowingPassageReview(false);
    setDeferredAnswers({});
    // Move to next question after the passage group
    if (currentPassageGroup) {
      const nextIdx = currentPassageGroup.endIdx + 1;
      if (nextIdx >= filteredQuestions.length) {
        // Passage group was the last set — show session complete
        setSessionComplete(true);
      } else {
        setIdx(nextIdx);
        updateSessionProgress(nextIdx);
      }
    }
  }, [currentPassageGroup, filteredQuestions.length, updateSessionProgress]);

  // Swipe tutorial
  const { showTutorial, dismissTutorial } = useSwipeTutorial();

  // Early returns AFTER all hooks
  // When resuming an exam session, we need parentExamId to fetch all sections' questions.
  // Show loading until we have it (or until we know it's not an exam session).
  const waitingForExamLookup = contextType === 'exam' && sessionId && !parentExamId && !sectionLookupData;
  if (isLoading || waitingForExamLookup) return (
    <div className="mx-auto max-w-lg px-4 pt-4 space-y-4">
      <Skeleton className="h-2.5 w-full rounded-full" />
      <Skeleton className="h-4 w-32 mx-auto rounded-full" />
      <Skeleton className="h-40 w-full rounded-3xl" />
      <div className="space-y-2.5">
        <Skeleton className="h-14 w-full rounded-2xl" />
        <Skeleton className="h-14 w-full rounded-2xl" />
        <Skeleton className="h-14 w-full rounded-2xl" />
        <Skeleton className="h-14 w-full rounded-2xl" />
      </div>
    </div>
  );
  if (!filteredQuestions.length) return (
    <div className="flex items-center justify-center min-h-[60vh] px-6">
      <FadeIn className="text-center">
        <div className="w-14 h-14 rounded-2xl bg-teal-50 border border-teal-100 flex items-center justify-center mx-auto mb-3">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-7 h-7 text-squishy-tealDark" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
          </svg>
        </div>
        <p className="font-semibold text-squishy-dark">No questions yet</p>
        <p className="text-sm text-slate-400 mt-1">Check back soon for this section.</p>
      </FadeIn>
    </div>
  );
  const q = filteredQuestions[Math.min(idx, filteredQuestions.length - 1)];
  if (!q) return <div className="flex items-center justify-center min-h-[60vh] text-slate-500">Loading...</div>;
  const hasPassage = !!(q.payload as any)?.passage;
  // Swipeable: everything except passage-based questions
  const isSwipeable = !hasPassage;

  // Derived passage state (after early returns, these are safe non-hook computations)
  const isLastInPassageGroup = currentPassageGroup ? idx === currentPassageGroup.endIdx : false;
  const deferReveal = hasPassage && passageReviewMode === 'end' && !showingPassageReview;
  const passageQuestionIndex = currentPassageGroup ? idx - currentPassageGroup.startIdx + 1 : 0;
  const passageQuestionCount = currentPassageGroup ? currentPassageGroup.endIdx - currentPassageGroup.startIdx + 1 : 0;

  return (
    <div className={`mx-auto px-4 pb-24 ${hasPassage ? 'study-wide max-w-lg lg:max-w-[90rem] lg:px-8' : 'max-w-lg'}`}>
      <div className="flex items-center gap-3 mb-3 -mx-1">
        <Link to={backPath} className="flex items-center gap-1 text-sm text-squishy-primary font-medium active:opacity-70 transition-opacity py-2 px-1">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
          </svg>
          Back
        </Link>
        {enrollment && (
          <div className="flex-1 min-w-0 text-right">
            <div className="text-sm font-semibold text-squishy-dark truncate">{enrollment.class_code}{enrollment.class_name ? ` \u00b7 ${enrollment.class_name}` : ''}</div>
            {enrollment.professor_name && <div className="text-xs text-slate-400 truncate">Prof. {enrollment.professor_name}</div>}
          </div>
        )}
      </div>

      {/* Progress bar */}
      {!sessionComplete && (
        <div className="mb-4">
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-squishy-teal to-squishy-tealDark rounded-full"
                initial={false}
                animate={{ width: `${Math.round(((idx + 1) / filteredQuestions.length) * 100)}%` }}
                transition={springs.soft}
              />
            </div>
            <span className="text-[11px] text-slate-400 font-bold tabular-nums shrink-0">
              {idx + 1} / {filteredQuestions.length}
            </span>
          </div>
          {sessionId && sessionAnswered > 0 && (
            <div className="mt-1 text-right text-[10px] text-slate-400 font-semibold">
              {Math.round((sessionCorrectCount / sessionAnswered) * 100)}% correct so far
            </div>
          )}
        </div>
      )}

      {/* Passage review mode toggle — only for passage-based questions */}
      {hasPassage && !showingPassageReview && (
        <div className="flex items-center justify-center mb-3">
          <div className="inline-flex items-center bg-slate-100 rounded-full p-1">
            <button
              onClick={() => { haptic.light(); setPassageReviewMode('immediate'); }}
              className={`text-xs font-semibold px-3.5 py-2 rounded-full transition-all ${
                passageReviewMode === 'immediate'
                  ? 'bg-white text-squishy-tealDark shadow-card'
                  : 'text-slate-500'
              }`}
            >Show answers now</button>
            <button
              onClick={() => { haptic.light(); setPassageReviewMode('end'); }}
              className={`text-xs font-semibold px-3.5 py-2 rounded-full transition-all ${
                passageReviewMode === 'end'
                  ? 'bg-white text-squishy-tealDark shadow-card'
                  : 'text-slate-500'
              }`}
            >Review at end</button>
          </div>
        </div>
      )}

      {!sessionComplete && <TopicBadge payload={q.payload as any} />}
      {!sessionComplete && currentPassageGroup && (
        <div className="text-xs text-indigo-400 font-semibold mb-3 text-center">
          Passage question {passageQuestionIndex} of {passageQuestionCount}
        </div>
      )}

      {sessionComplete ? (
        <SessionCompleteScreen
          sessionId={sessionId}
          startedAt={sessionData?.session?.startedAt ?? null}
          totalQuestions={filteredQuestions.length}
          sessionResults={sessionResultsRef.current}
          correctCount={sessionCorrectCount}
          answeredCount={sessionAnswered}
          xpEarned={sessionXpEarned}
          backPath={backPath}
          completeSession={completeSession}
          navigate={navigate}
        />
      ) : showingPassageReview && currentPassageGroup ? (
        <PassageReviewScreen
          passage={currentPassageGroup.passage}
          questions={passageReviewQuestions}
          answers={deferredAnswers}
          onContinue={handleExitReview}
        />
      ) : (
        <QuestionCard
          key={q.id}
          q={q}
          correctStreak={correctStreak}
          isSwipeable={isSwipeable}
          savedState={answeredState[q.id]}
          onSaveState={(s) => setAnsweredState(prev => ({ ...prev, [q.id]: s }))}
          onNext={() => {
            if (deferReveal && isLastInPassageGroup) {
              setShowingPassageReview(true);
              return;
            }
            if (idx >= filteredQuestions.length - 1) {
              setSessionComplete(true);
              return;
            }
            const newIdx = idx + 1;
            setIdx(newIdx);
            updateSessionProgress(newIdx);
          }}
          onSwipeLeft={() => {
            // Mark for review, then advance
            if (!isFlagged(q.id)) toggleFlaggedQuestion(q.id);
            if (idx >= filteredQuestions.length - 1) {
              setSessionComplete(true);
              return;
            }
            const newIdx = idx + 1;
            setIdx(newIdx);
            updateSessionProgress(newIdx);
          }}
          onPrev={() => setIdx((i) => Math.max(i - 1, 0))}
          onResult={handleAttemptResult}
          sessionId={sessionId}
          deferReveal={deferReveal}
          onDeferredAnswer={handleDeferredAnswer}
          isLastInPassageGroup={isLastInPassageGroup}
        />
      )}

      {showTutorial && isSwipeable && <SwipeTutorialOverlay onDismiss={dismissTutorial} />}
    </div>
  );
}

/** Shorthand: apply Fahrenheit conversion to any displayed text */
const t = addFahrenheit;

function TopicBadge({ payload }: { payload: any }) {
  if (!payload.subject && !payload.topic) return null;
  return (
    <div className="flex flex-wrap gap-1 mb-2 justify-center">
      {payload.subject && <span className="text-xs bg-blue-100 dark:bg-blue-500/20 text-blue-700 px-2 py-0.5 rounded-full">{payload.subject}</span>}
      {payload.topic && <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{payload.topic}</span>}
      {payload.subtopic && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{payload.subtopic}</span>}
    </div>
  );
}

function QuestionCard({ q, onNext, onSwipeLeft, onPrev, onResult, sessionId, deferReveal, onDeferredAnswer, isLastInPassageGroup, isSwipeable, savedState, onSaveState, correctStreak = 0 }: {
  q: ApiQuestion; onNext: () => void; onSwipeLeft?: () => void; onPrev: () => void;
  onResult: (r: any, questionId?: string, phase?: 'local' | 'server') => void; sessionId: string | null;
  deferReveal?: boolean; onDeferredAnswer?: (qId: string, selected: number[], correct: boolean, result: any) => void;
  isLastInPassageGroup?: boolean; isSwipeable?: boolean; correctStreak?: number;
  savedState?: { selected: number[]; revealed: boolean; locked?: boolean; result: any };
  onSaveState?: (s: { selected: number[]; revealed: boolean; locked?: boolean; result: any }) => void;
}) {
  const submit = useSubmitAttempt();
  const [selected, setSelected] = useState<number[]>(savedState?.selected ?? []);
  const [revealed, setRevealed] = useState(savedState?.revealed ?? false);
  const [locked, setLocked] = useState(savedState?.locked ?? false); // answer locked in but not revealed (deferred mode)
  const [result, setResult] = useState<any>(savedState?.result ?? null);
  const [flagged, setFlagged] = useState(() => isFlagged(q.id));
  const questionStartRef = useRef<number>(Date.now());

  const payload = q.payload as any;
  const isSata = q.type === 'sata';

  useEffect(() => {
    setSelected(savedState?.selected ?? []);
    setRevealed(savedState?.revealed ?? false);
    setLocked(savedState?.locked ?? false);
    setResult(savedState?.result ?? null);
    questionStartRef.current = Date.now();
  }, [q.id, savedState]);

  const [submitting, setSubmitting] = useState(false);
  // A ref, not the state flag: two taps in the same tick both read the stale
  // `submitting` closure, and now that the POST is fired in the background that
  // would record the attempt twice and double the XP.
  const submittedRef = useRef(false);
  useEffect(() => { submittedRef.current = Boolean(savedState?.revealed || savedState?.locked); }, [q.id, savedState]);

  const doSubmit = useCallback((selectedAnswers: number[]) => {
    if (submittedRef.current || submitting || revealed || locked) return;
    submittedRef.current = true;
    setSubmitting(true);

    // Correctness is computed entirely client-side from the payload, so the
    // reveal happens NOW and never waits on the network. Recording the attempt
    // (XP + stats) is fired off separately below and reconciled when it lands.
    // Previously this awaited the POST before revealing, which is what made
    // feedback feel laggy on a slow connection.
    let correct = false;
    if (isSata) {
      const c = new Set<number>(payload.correct);
      const s = new Set(selectedAnswers);
      correct = c.size === s.size && [...c].every((x) => s.has(x));
    } else {
      correct = selectedAnswers[0] === payload.correct;
    }

    const timeMs = Date.now() - questionStartRef.current;
    const pendingResult = { correct, pending: true };

    if (deferReveal) {
      haptic.medium();
      setLocked(true);
      setResult(pendingResult);
      onDeferredAnswer?.(q.id, selectedAnswers, correct, pendingResult);
      onSaveState?.({ selected: selectedAnswers, revealed: false, locked: true, result: pendingResult });
    } else {
      if (correct) haptic.success(); else haptic.error();
      setRevealed(true);
      setResult(pendingResult);
      onSaveState?.({ selected: selectedAnswers, revealed: true, result: pendingResult });
    }
    // Streak, session counts and the results screen are settled from the local
    // grade so they stay correct even if the attempt never reaches the server.
    onResult(pendingResult, q.id, 'local');
    setSubmitting(false);

    // Record in the background. recordAttempt retries transient failures and
    // parks the attempt in a durable queue if it still cannot be saved, so a
    // dropped request no longer silently costs the student their XP.
    recordAttempt(
      submit.mutateAsync,
      { clientAttemptId: newAttemptId(), questionId: q.id, correct, firstTry: true, selectedAnswers, studySessionId: sessionId, timeMs },
    ).then((r) => {
      const merged = { ...r, correct };
      setResult(merged);
      onResult(merged, q.id, 'server');
      if (deferReveal) onDeferredAnswer?.(q.id, selectedAnswers, correct, merged);
      onSaveState?.({
        selected: selectedAnswers,
        revealed: !deferReveal,
        locked: deferReveal || undefined,
        result: merged,
      });
    }).catch((err) => {
      // Queued for retry; tell the user rather than showing a misleading "0 XP".
      console.error('[study] attempt could not be saved, queued for retry:', err);
      const failed = { correct, saveFailed: true };
      setResult(failed);
      onSaveState?.({
        selected: selectedAnswers,
        revealed: !deferReveal,
        locked: deferReveal || undefined,
        result: failed,
      });
    });
  }, [submitting, revealed, locked, isSata, payload, q.id, submit, onResult, sessionId, deferReveal, onDeferredAnswer, onSaveState]);

  const tapOption = (i: number) => {
    // submittedRef, not just `revealed`: setSelected below runs before doSubmit
    // can bail, so without this a same-tick second tap moves the highlight off
    // the option that was actually graded.
    if (revealed || locked || submittedRef.current) return;
    haptic.light();
    if (isSata) {
      setSelected((cur) => cur.includes(i) ? cur.filter(x => x !== i) : [...cur, i]);
    } else {
      setSelected([i]);
      doSubmit([i]);
    }
  };

  const reset = () => {
    setSelected([]);
    setRevealed(false);
    setLocked(false);
    setResult(null);
    onNext();
  };

  const passage = payload.passage;

  // Visual helpers only: derive per-option and overall correctness for styling
  const isCorrectOption = (i: number) => Array.isArray(payload.correct) ? payload.correct.includes(i) : i === payload.correct;
  const wasCorrect = revealed && (isSata
    ? (() => {
        const c = new Set<number>(Array.isArray(payload.correct) ? payload.correct : [payload.correct]);
        return c.size === selected.length && selected.every((x) => c.has(x));
      })()
    : selected[0] === payload.correct);

  // Question + options + explanation (shared between layouts)
  const questionContent = (
    <>
      <div className="text-[16px] whitespace-pre-wrap leading-relaxed text-squishy-dark">{t(payload.question)}</div>

      {payload.options && (
        <ul className="mt-5 space-y-2.5" aria-label="Answer options">
          {payload.options.map((opt: string, i: number) => {
            const isSelected = selected.includes(i);
            const isCorrect = isCorrectOption(i);
            const isWrongPick = revealed && isSelected && !isCorrect;
            const interactive = !revealed && !locked;

            let classes = 'w-full flex items-start gap-3 text-left border-2 rounded-2xl px-4 py-3.5 min-h-[52px] transition-colors duration-150';
            if (revealed) {
              if (isCorrect) classes += ' border-emerald-400 bg-emerald-50';
              else if (isWrongPick) classes += ' border-red-300 bg-red-50 animate-shake';
              else classes += ' border-slate-100 bg-white opacity-50';
            } else if (locked) {
              // Deferred mode: show neutral locked-in state
              classes += isSelected ? ' border-slate-300 bg-slate-50' : ' border-slate-100 bg-white opacity-50';
            } else {
              classes += isSelected
                ? ' border-squishy-teal bg-teal-50 shadow-glow'
                : ' border-slate-200 bg-white shadow-card';
            }

            // SATA gets square checkbox-style badges; single answer gets round ones
            let badge = `mt-0.5 w-6 h-6 ${isSata ? 'rounded-md' : 'rounded-full'} flex items-center justify-center text-[11px] font-bold shrink-0 transition-colors duration-150`;
            if (revealed && isCorrect) badge += ' bg-emerald-500 text-white';
            else if (isWrongPick) badge += ' bg-red-400 text-white';
            else if (isSelected) badge += ' bg-squishy-teal text-white';
            else badge += ' bg-slate-100 text-slate-500';

            return (
              <li key={i}>
                <motion.button
                  type="button"
                  onClick={() => tapOption(i)}
                  disabled={!interactive}
                  aria-pressed={isSelected}
                  whileTap={interactive ? { scale: 0.97 } : undefined}
                  animate={revealed && isCorrect ? { scale: [1, 1.03, 1] } : undefined}
                  transition={revealed && isCorrect ? { duration: 0.4, ease: 'easeOut' } : springs.snappy}
                  className={classes}
                >
                  <span className={badge} aria-hidden>{String.fromCharCode(65 + i)}</span>
                  <span className="flex-1 text-[15px] leading-relaxed">{t(opt)}</span>
                  {revealed && isCorrect && (
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 mt-0.5 text-emerald-500 shrink-0" aria-hidden>
                      <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                    </svg>
                  )}
                  {isWrongPick && (
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 mt-0.5 text-red-400 shrink-0" aria-hidden>
                      <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clipRule="evenodd" />
                    </svg>
                  )}
                  {locked && isSelected && (
                    <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mt-1.5 shrink-0">Locked</span>
                  )}
                </motion.button>
              </li>
            );
          })}
        </ul>
      )}

      <AnimatePresence initial={false}>
        {revealed && (
          <motion.div
            key="reveal"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            {/* Result banner */}
            <div className={`mt-4 flex items-center gap-3 rounded-2xl border-2 px-4 py-3 ${wasCorrect ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ ...springs.bouncy, delay: 0.05 }}
                className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${wasCorrect ? 'bg-emerald-500' : 'bg-red-400'}`}
              >
                {wasCorrect ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden><path d="M5 13l4 4L19 7" /></svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
                )}
              </motion.div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-display font-bold text-base ${wasCorrect ? 'text-emerald-700' : 'text-red-600'}`}>
                    {wasCorrect ? (correctStreak >= 5 ? 'On fire!' : 'Correct!') : 'Not quite'}
                  </span>
                  {wasCorrect && correctStreak >= 2 && (
                    <motion.span
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ ...springs.bouncy, delay: 0.2 }}
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-600 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3" aria-hidden>
                        <path fillRule="evenodd" d="M12.963 2.286a.75.75 0 00-1.071-.136 9.742 9.742 0 00-3.539 6.177A7.547 7.547 0 016.648 6.61a.75.75 0 00-1.152-.082A9 9 0 1015.68 4.534a7.46 7.46 0 01-2.717-2.248zM15.75 14.25a3.75 3.75 0 11-7.313-1.172c.628.465 1.35.81 2.133 1a5.99 5.99 0 011.925-3.545 3.75 3.75 0 013.255 3.717z" clipRule="evenodd" />
                      </svg>
                      {correctStreak} in a row
                    </motion.span>
                  )}
                </div>
                {result && (
                  <div className={`text-xs font-semibold ${wasCorrect ? 'text-emerald-600' : 'text-red-500'}`}>
                    {/* Three distinct states. "0 XP" used to be shown for an
                        attempt the server never recorded, which read as "you
                        earned nothing" for an answer that was actually right. */}
                    {result.saveFailed ? (
                      <span className="text-amber-600">Not saved yet, will retry</span>
                    ) : typeof result.xpEarned !== 'number' ? (
                      <span className="text-slate-400">Saving…</span>
                    ) : (
                      <>
                        {result.xpEarned > 0 ? `+${result.xpEarned} XP` : `${result.xpEarned} XP`}
                        {result.speedBonus ? <span className="text-amber-500 ml-1.5">+{result.speedBonus} speed</span> : null}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            <ExplanationBlock payload={payload} result={result} questionStats={(q as any).firstAttemptStats} />
            <ProExplanationBlock payload={payload} questionId={q.id} />
            <CommentsSection questionId={q.id} />
          </motion.div>
        )}
      </AnimatePresence>

      {locked && (
        <div className="mt-4 flex items-center justify-center rounded-2xl bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-500 text-center">
          Answer locked in. You'll review all answers at the end of this passage.
        </div>
      )}

      <div className="mt-6 space-y-3">
        {isSata && !revealed && !locked && (
          <Pressable
            haptic="medium"
            disabled={selected.length === 0 || submitting}
            onClick={() => doSubmit(selected)}
            className="w-full bg-gradient-to-r from-squishy-teal to-squishy-tealDark text-white py-3.5 rounded-2xl text-base font-bold shadow-pop disabled:opacity-40 disabled:shadow-none transition-opacity"
          >{submitting ? 'Checking...' : 'Check Answer'}</Pressable>
        )}
        <AnimatePresence initial={false}>
          {revealed && (
            <motion.div
              key="continue"
              initial={{ opacity: 0, y: 14, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ ...springs.bouncy, delay: 0.1 }}
            >
              <Pressable
                haptic="medium"
                onClick={reset}
                className="w-full bg-gradient-to-r from-squishy-teal to-squishy-tealDark text-white py-3.5 rounded-2xl text-base font-bold shadow-pop"
              >
                Continue {'\u2192'}
              </Pressable>
            </motion.div>
          )}
        </AnimatePresence>
        {locked && !revealed && (
          <Pressable
            haptic="medium"
            onClick={reset}
            className="w-full bg-squishy-navy text-white py-3.5 rounded-2xl text-base font-bold shadow-card"
          >
            {isLastInPassageGroup ? 'Review Passage' : 'Next Question'} {'\u2192'}
          </Pressable>
        )}
        <div className="flex items-center justify-between px-1">
          <button onClick={onPrev} className="text-sm font-medium text-slate-400 active:text-slate-600 py-2.5 pr-3 transition-colors">{'\u2190'} Previous</button>
          {!revealed && !locked && (
            <button onClick={reset} className="text-sm font-medium text-slate-400 active:text-slate-600 py-2.5 pl-3 transition-colors">Skip {'\u2192'}</button>
          )}
        </div>
      </div>
    </>
  );

  // Desktop side-by-side layout for passage-based questions
  if (passage) {
    return (
      <div className="lg:grid lg:grid-cols-[3fr_2fr] lg:gap-6 lg:items-start">
        {/* Passage panel — sticky on desktop */}
        <div className="lg:sticky lg:top-4 mb-4 lg:mb-0">
          <PassageBlock text={passage} desktopExpanded />
        </div>
        {/* Question panel */}
        <motion.div
          className="bg-white rounded-3xl shadow-card border border-slate-100 p-5 relative"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springs.soft}
        >
          <FlagButton flagged={flagged} onToggle={() => { toggleFlaggedQuestion(q.id); setFlagged(!flagged); }} questionId={q.id} />
          <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider text-squishy-tealDark bg-teal-50 border border-teal-100 rounded-full px-2.5 py-1 mb-3">
            {isSata ? 'Select All That Apply' : 'Multiple Choice'}
          </span>
          {/* Mobile-only inline passage (hidden on desktop since it's in left panel) */}
          <div className="lg:hidden">
            {/* passage already shown above on mobile via the grid fallback (single column) */}
          </div>
          {questionContent}
        </motion.div>
      </div>
    );
  }

  // Standard single-column layout (no passage)
  const card = (
    <motion.div
      className="bg-white rounded-3xl shadow-card border border-slate-100 p-5 relative"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springs.soft}
    >
      <FlagButton flagged={flagged} onToggle={() => { toggleFlaggedQuestion(q.id); setFlagged(!flagged); }} questionId={q.id} />
      <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider text-squishy-tealDark bg-teal-50 border border-teal-100 rounded-full px-2.5 py-1 mb-3">
        {isSata ? 'Select All That Apply' : 'Multiple Choice'}
      </span>
      {questionContent}
    </motion.div>
  );

  if (isSwipeable) {
    return (
      <SwipeCard
        enabled={revealed}
        onSwipeRight={reset}
        onSwipeLeft={() => onSwipeLeft?.()}
      >
        {card}
      </SwipeCard>
    );
  }

  return card;
}

function PassageReviewScreen({ passage, questions, answers, onContinue }: {
  passage: string;
  questions: ApiQuestion[];
  answers: Record<string, { selected: number[]; correct: boolean; result: any }>;
  onContinue: () => void;
}) {
  const correctCount = Object.values(answers).filter(a => a.correct).length;
  const totalXp = Object.values(answers).reduce((sum, a) => sum + (a.result?.xpEarned ?? 0), 0);

  return (
    <div className="lg:grid lg:grid-cols-[3fr_2fr] lg:gap-6 lg:items-start">
      {/* Passage (sticky on desktop) */}
      <div className="lg:sticky lg:top-4 mb-4 lg:mb-0">
        <PassageBlock text={passage} desktopExpanded />
      </div>

      {/* Review panel */}
      <Stagger className="space-y-4">
        {/* Score summary */}
        <Stagger.Item>
        <div className="bg-white rounded-3xl shadow-card border border-slate-100 p-5 text-center">
          <div className="font-display text-lg font-bold text-squishy-dark mb-1">Passage Review</div>
          <div className="text-4xl font-display font-bold mb-1">
            <span className={correctCount === questions.length ? 'text-emerald-600' : correctCount > 0 ? 'text-amber-500' : 'text-red-500'}>
              {correctCount}
            </span>
            <span className="text-slate-300 mx-1">/</span>
            <span className="text-slate-500">{questions.length}</span>
          </div>
          <div className="text-sm text-slate-400">
            {Math.round((correctCount / Math.max(questions.length, 1)) * 100)}% correct
            {totalXp > 0 && <span className="text-emerald-600 font-semibold ml-2">+{totalXp} XP</span>}
          </div>
        </div>
        </Stagger.Item>

        {/* Each question with reveal */}
        {questions.map((q, qi) => {
          const payload = q.payload as any;
          const answer = answers[q.id];
          const selectedSet = new Set(answer?.selected ?? []);

          return (
            <Stagger.Item key={q.id}>
            <div className="bg-white rounded-3xl shadow-card border border-slate-100 p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  answer?.correct ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'
                }`}>
                  {answer?.correct ? '\u2713' : '\u2717'}
                </span>
                <span className="text-xs text-slate-400 font-semibold">Question {qi + 1}</span>
              </div>

              <div className="text-sm whitespace-pre-wrap leading-relaxed mb-3">{t(payload.question)}</div>

              {payload.options && (
                <ul className="space-y-1.5 mb-3">
                  {payload.options.map((opt: string, i: number) => {
                    const wasSelected = selectedSet.has(i);
                    const isCorrect = Array.isArray(payload.correct) ? payload.correct.includes(i) : i === payload.correct;
                    const isWrongPick = wasSelected && !isCorrect;

                    let cls = 'border rounded-lg p-2.5 text-sm';
                    if (isCorrect) cls += ' border-green-400 bg-green-50';
                    else if (isWrongPick) cls += ' border-red-300 bg-red-50';
                    else cls += ' border-slate-100 text-slate-400';

                    return (
                      <li key={i} className={cls}>
                        <span className="font-mono text-xs mr-1.5 text-slate-400">{String.fromCharCode(65 + i)}.</span>
                        {t(opt)}
                        {isCorrect && <span className="ml-1.5 text-green-600 text-xs font-semibold">{'\u2713'}</span>}
                        {isWrongPick && <span className="ml-1.5 text-red-500 text-xs font-semibold">{'\u2717'} Your answer</span>}
                      </li>
                    );
                  })}
                </ul>
              )}

              <ExplanationBlock payload={payload} result={answer?.result} questionStats={(q as any).firstAttemptStats} />
              <ProExplanationBlock payload={payload} questionId={q.id} />
            </div>
            </Stagger.Item>
          );
        })}

        {/* Continue button */}
        <Stagger.Item>
        <div className="py-2">
          <Pressable
            haptic="medium"
            onClick={onContinue}
            className="w-full bg-gradient-to-r from-squishy-teal to-squishy-tealDark text-white py-3.5 rounded-2xl text-base font-bold shadow-pop"
          >
            Continue {'\u2192'}
          </Pressable>
        </div>
        </Stagger.Item>
      </Stagger>
    </div>
  );
}

function PassageBlock({ text, desktopExpanded }: { text: string; desktopExpanded?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  // Highlighted word indices stored as a Set
  const [highlighted, setHighlighted] = useState<Set<number>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const converted = t(text);
  const isLong = converted.length > 600;
  // On desktop (desktopExpanded), always show full passage
  const effectiveExpanded = desktopExpanded || expanded;

  // Split into word tokens preserving whitespace
  const tokens = useMemo(() => {
    const result: { text: string; isWord: boolean }[] = [];
    const parts = converted.split(/(\s+)/);
    for (const p of parts) {
      result.push({ text: p, isWord: /\S/.test(p) });
    }
    return result;
  }, [converted]);

  // Find the contiguous highlighted group containing a word index
  const getHighlightGroup = useCallback((idx: number): number[] => {
    if (!highlighted.has(idx)) return [];
    const group: number[] = [idx];
    // Expand backward
    let i = idx - 1;
    while (highlighted.has(i)) { group.unshift(i); i--; }
    // Expand forward
    i = idx + 1;
    while (highlighted.has(i)) { group.push(i); i++; }
    return group;
  }, [highlighted]);

  // Handle drag-to-highlight via native text selection
  const handleSelectionEnd = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !containerRef.current) return;

    const range = sel.getRangeAt(0);
    if (!containerRef.current.contains(range.commonAncestorContainer)) return;

    // Find all word spans within the selection range
    const wordSpans = containerRef.current.querySelectorAll('[data-wi]');
    const newHighlights = new Set(highlighted);
    let added = false;

    for (const span of wordSpans) {
      if (sel.containsNode(span, true)) {
        const wi = parseInt(span.getAttribute('data-wi')!, 10);
        if (!isNaN(wi)) {
          newHighlights.add(wi);
          added = true;
        }
      }
    }

    if (added) {
      setHighlighted(newHighlights);
    }
    sel.removeAllRanges();
  }, [highlighted]);

  // Tap a highlighted word to unhighlight its entire contiguous group
  const handleWordClick = useCallback((wi: number, e: React.MouseEvent) => {
    e.stopPropagation();
    // Only unhighlight — if word is highlighted, remove its group
    if (highlighted.has(wi)) {
      const group = getHighlightGroup(wi);
      const next = new Set(highlighted);
      for (const idx of group) next.delete(idx);
      setHighlighted(next);
    }
    // Single-word tap to highlight is NOT supported anymore (use drag instead)
  }, [highlighted, getHighlightGroup]);

  const clearHighlights = useCallback(() => setHighlighted(new Set()), []);
  const hasHighlights = highlighted.size > 0;

  // Word index counter (reset each render)
  let wordIndex = -1;

  return (
    <div className={`rounded-xl border border-indigo-200 overflow-hidden ${desktopExpanded ? 'bg-white' : 'mb-4'}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-indigo-50 text-xs font-semibold text-indigo-700 uppercase tracking-wide"
      >
        <span>Passage {hasHighlights ? `(${highlighted.size} highlighted)` : ''}</span>
        {!desktopExpanded && (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
            className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
          >
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        )}
      </button>
      <div className={`px-4 py-3 text-sm leading-relaxed text-slate-700 bg-white transition-all ${
        !effectiveExpanded && isLong ? 'max-h-48 overflow-hidden relative' : desktopExpanded ? 'max-h-[80vh] overflow-y-auto' : ''
      }`}>
        <div
          ref={containerRef}
          className="whitespace-pre-wrap"
          onMouseUp={handleSelectionEnd}
          onTouchEnd={handleSelectionEnd}
        >
          {tokens.map((tok, i) => {
            if (!tok.isWord) {
              return <span key={i}>{tok.text}</span>;
            }
            wordIndex++;
            const wi = wordIndex;
            const isHl = highlighted.has(wi);
            return (
              <span
                key={i}
                data-wi={wi}
                onClick={(e) => handleWordClick(wi, e)}
                className={`rounded-sm transition-colors ${
                  isHl ? 'bg-yellow-200 text-yellow-900 cursor-pointer' : ''
                }`}
              >{tok.text}</span>
            );
          })}
        </div>
        {!effectiveExpanded && isLong && (
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white dark:from-slate-800 to-transparent pointer-events-none" />
        )}
      </div>
      {!effectiveExpanded && isLong && (
        <button
          onClick={() => setExpanded(true)}
          className="w-full text-center text-xs text-indigo-600 font-medium py-2 bg-white border-t border-indigo-100 hover:bg-indigo-50"
        >
          Show full passage
        </button>
      )}
      {hasHighlights && (
        <div className="flex items-center justify-center gap-3 py-1.5 bg-white border-t border-indigo-100">
          <span className="text-[10px] text-slate-400">Tap highlighted text to remove</span>
          <button
            onClick={clearHighlights}
            className="text-[10px] text-red-400 font-medium hover:text-red-600"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}

function ProExplanationBlock({ payload, questionId }: { payload: any; questionId: string }) {
  // pro_explanation may already be present (from GET /questions/:id) or stripped from feed
  const hasProInline = payload.pro_explanation && payload.pro_explanation.trim().length > 0;
  const hasProFlag = payload.has_pro_explanation;
  const [expanded, setExpanded] = useState(false);
  // Lazy-load full question only when user expands and inline text is missing
  const needsFetch = expanded && !hasProInline && hasProFlag;
  const { data: detail } = useQuestionDetail(needsFetch ? questionId : null);
  const proText = hasProInline
    ? payload.pro_explanation
    : (detail?.question?.payload as any)?.pro_explanation;

  if (!hasProInline && !hasProFlag) return null;
  return (
    <div className="mt-4 rounded-2xl border-2 border-amber-300 dark:border-amber-500/30 bg-gradient-to-br from-amber-50 via-amber-50/80 to-orange-50/60 dark:from-amber-500/10 dark:via-amber-500/10 dark:to-orange-500/10 overflow-hidden shadow-sm">
      <button
        onClick={() => { haptic.light(); setExpanded(!expanded); }}
        className="w-full flex items-center justify-between px-4 py-3.5 active:bg-amber-100/40 dark:active:bg-amber-500/20 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <span className="text-[11px] font-extrabold text-white bg-gradient-to-r from-amber-500 to-orange-500 px-2 py-0.5 rounded-md shadow-sm tracking-wide">PRO</span>
          <span className="text-sm font-bold text-slate-800">Deep Dive Explanation</span>
        </div>
        <div className="flex items-center gap-1.5">
          {!expanded && <span className="text-[11px] text-amber-600 font-medium hidden sm:block">Every answer explained</span>}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
            className={`w-5 h-5 text-amber-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
          >
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </div>
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="pro-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed border-t border-amber-200/60">
              {proText
                ? formatExplanation(proText, payload)
                : <p className="text-slate-400 italic">Loading...</p>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function formatExplanation(text: string, payload: any): React.ReactNode {
  const converted = t(text);
  const notes: string[] = [];
  const body = converted
    .replace(/(?:^|\n)\s*(Key takeaway:[^\n]+)(?=\n|$)/gi, (_match, note) => {
      notes.push(note.trim());
      return '\n';
    })
    .replace(/(?:^|\n)\s*(Source:[^\n]+)(?=\n|$)/gi, (_match, note) => {
      notes.push(note.trim());
      return '\n';
    })
    .trim();

  // Determine which option indices are correct from the payload
  const correctIndices = new Set<number>(
    Array.isArray(payload.correct) ? payload.correct : (typeof payload.correct === 'number' ? [payload.correct] : [])
  );

  // Try to split on option-letter patterns:
  //   "A is correct/incorrect", "A. ...", "A) ...", "Option A:", "(A)", etc.
  const optionPattern = /(?:^|\n)\s*(?:Option\s+)?([A-F])[\s).:]+/gi;
  const splitPattern = /\b([A-F])\s+is\s+(correct|incorrect|wrong|right)\b/gi;

  // Collect all split points from both patterns
  const splits: { pos: number; letter: string; matchLen: number }[] = [];

  let m: RegExpExecArray | null;
  // Pattern 1: "A is correct/incorrect"
  splitPattern.lastIndex = 0;
  while ((m = splitPattern.exec(body)) !== null) {
    splits.push({ pos: m.index, letter: m[1]!.toUpperCase(), matchLen: m[0].length });
  }
  // Pattern 2: "A. ...", "A) ...", "Option A:" — only if pattern 1 found nothing
  if (splits.length === 0) {
    optionPattern.lastIndex = 0;
    while ((m = optionPattern.exec(body)) !== null) {
      splits.push({ pos: m.index, letter: m[1]!.toUpperCase(), matchLen: m[0].length });
    }
  }

  // If no letter-based option patterns found, try matching paragraphs that start
  // with actual option text (e.g. "Option text here — explanation why wrong...")
  const opts: string[] = payload.options ?? [];
  if (splits.length < 2 && opts.length > 0) {
    const paragraphs = body.split(/\n\n+/).filter(Boolean);
    const optionParas: { optIdx: number; text: string }[] = [];
    const introParagraphs: string[] = [];

    for (const para of paragraphs) {
      const paraLower = para.toLowerCase().slice(0, 120);
      let matched = -1;
      let bestLen = 0;
      for (let oi = 0; oi < opts.length; oi++) {
        const optWords = opts[oi]!.split(/\s+/).slice(0, 8).join(' ').toLowerCase();
        if (optWords.length >= 10 && paraLower.startsWith(optWords.slice(0, Math.min(optWords.length, 60)))) {
          if (optWords.length > bestLen) { bestLen = optWords.length; matched = oi; }
        }
      }
      if (matched >= 0) {
        let explanation = para;
        const dashIdx = para.indexOf(' — ');
        const hyphenIdx = para.indexOf(' - ');
        const optLen = opts[matched]!.length;
        if (dashIdx > 0 && dashIdx < optLen + 30) {
          explanation = para.slice(dashIdx + 3).trim();
        } else if (hyphenIdx > 0 && hyphenIdx < optLen + 30) {
          explanation = para.slice(hyphenIdx + 3).trim();
        }
        optionParas.push({ optIdx: matched, text: explanation });
      } else {
        introParagraphs.push(para);
      }
    }

    if (optionParas.length >= 2) {
      return (
        <div className="space-y-2">
          {introParagraphs.map((p, i) => <p key={`intro-${i}`}>{p.trim()}</p>)}
          {optionParas.map((seg, i) => {
            const isCorrectOpt = correctIndices.has(seg.optIdx);
            const displayLetter = String.fromCharCode(65 + seg.optIdx);
            return (
              <div key={`opt-${i}`} className={`flex gap-2 p-2.5 rounded-lg ${
                isCorrectOpt ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                <span className={`font-bold text-sm shrink-0 ${isCorrectOpt ? 'text-green-700' : 'text-red-500'}`}>
                  {displayLetter}.
                </span>
                <span className="text-sm">{seg.text}</span>
              </div>
            );
          })}
          <ExplanationNotes notes={notes} />
        </div>
      );
    }
  }

  // Fallback: just split into paragraphs by sentences or double newlines
  if (splits.length < 2) {
    const paragraphs = body.split(/\n\n+/).filter(Boolean);
    if (paragraphs.length <= 1) {
      // Split long single-paragraph explanations by sentences
      const sentences = body.split(/(?<=\.)\s+(?=[A-Z])/);
      if (sentences.length > 1) {
        return (
          <div className="space-y-2">
            {sentences.map((s, i) => <p key={i}>{s.trim()}</p>)}
            <ExplanationNotes notes={notes} />
          </div>
        );
      }
      return (
        <>
          <span>{body}</span>
          <ExplanationNotes notes={notes} />
        </>
      );
    }
    return (
      <div className="space-y-2">
        {paragraphs.map((p, i) => <p key={i}>{p.trim()}</p>)}
        <ExplanationNotes notes={notes} />
      </div>
    );
  }

  // Build segments from split positions
  splits.sort((a, b) => a.pos - b.pos);
  const segments: { letter: string | null; text: string }[] = [];
  // Any text before the first option
  if (splits[0]!.pos > 0) {
    segments.push({ letter: null, text: body.slice(0, splits[0]!.pos).trim() });
  }
  for (let i = 0; i < splits.length; i++) {
    const start = splits[i]!.pos;
    const end = i + 1 < splits.length ? splits[i + 1]!.pos : body.length;
    const raw = body.slice(start, end).trim();
    // Strip the leading "A is correct:" or "A. " prefix to get just the explanation text
    const cleaned = raw.replace(/^(?:Option\s+)?[A-F]\s*(?:is\s+(?:correct|incorrect|wrong|right)\s*[.:—-]?\s*|[).:]\s*)/i, '').trim();
    segments.push({ letter: splits[i]!.letter, text: cleaned });
  }

  // Match explanation segments to actual options by text similarity
  // to fix mismatched letter labels from AI generation
  const options: string[] = payload.options ?? [];
  const matchOptionIndex = (segText: string): number => {
    const lower = segText.toLowerCase().slice(0, 60);
    let bestIdx = -1, bestScore = 0;
    for (let oi = 0; oi < options.length; oi++) {
      const optLower = options[oi]!.toLowerCase();
      // Check if the explanation segment starts with or contains key words from the option
      const optWords = optLower.split(/\s+/).filter(w => w.length > 3);
      const matchCount = optWords.filter(w => lower.includes(w)).length;
      const score = optWords.length > 0 ? matchCount / optWords.length : 0;
      if (score > bestScore) { bestScore = score; bestIdx = oi; }
    }
    return bestScore >= 0.4 ? bestIdx : -1;
  };

  return (
    <div className="space-y-2">
      {segments.filter(s => s.text).map((seg, i) => {
        if (!seg.letter) return <p key={i}>{seg.text}</p>;
        // Try to match this explanation segment to an actual option
        const matchedIdx = matchOptionIndex(seg.text);
        const displayLetter = matchedIdx >= 0
          ? String.fromCharCode(65 + matchedIdx)
          : seg.letter;
        const isCorrect = matchedIdx >= 0
          ? correctIndices.has(matchedIdx)
          : correctIndices.has(seg.letter.charCodeAt(0) - 65);
        return (
          <div key={i} className={`flex gap-2 p-2.5 rounded-lg ${
            isCorrect ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            <span className={`font-bold text-sm shrink-0 ${isCorrect ? 'text-green-700' : 'text-red-500'}`}>
              {displayLetter}.
            </span>
            <span className="text-sm">{seg.text}</span>
          </div>
        );
      })}
      <ExplanationNotes notes={notes} />
    </div>
  );
}

function ExplanationNotes({ notes }: { notes: string[] }) {
  if (!notes.length) return null;
  return (
    <div className="space-y-1 pt-1">
      {notes.map((note, index) => (
        <p key={index} className="text-xs font-medium text-slate-600">{note}</p>
      ))}
    </div>
  );
}

// ── Comments Section ───────────────────────────────────────────────────────

function CommentsSection({ questionId }: { questionId: string }) {
  const { data, isLoading } = useQuestionComments(questionId);
  const postComment = usePostQuestionComment();
  const deleteComment = useDeleteQuestionComment();
  const voteComment = useVoteComment();
  const reportComment = useReportComment();
  const [expanded, setExpanded] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [reportingId, setReportingId] = useState<string | null>(null);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  // Track animated scores for floating effect
  const [animatingVotes, setAnimatingVotes] = useState<Record<string, 'up' | 'down'>>({});

  const comments = data?.comments ?? [];
  const topPreview = data?.topPreview ?? [];
  const totalCount = data?.totalCount ?? comments.length;
  const canPost = data?.entitlements?.canPost ?? false;
  const canVote = data?.entitlements?.canVote ?? false;
  const canSeeAll = data?.entitlements?.canSeeAll ?? false;

  const handlePost = async () => {
    if (!newComment.trim()) return;
    await postComment.mutateAsync({ questionId, body: newComment.trim() });
    setNewComment('');
  };

  const handleVote = async (commentId: string, value: number) => {
    // Trigger animation
    setAnimatingVotes(prev => ({ ...prev, [commentId]: value === 1 ? 'up' : 'down' }));
    setTimeout(() => setAnimatingVotes(prev => { const n = { ...prev }; delete n[commentId]; return n; }), 600);
    await voteComment.mutateAsync({ commentId, value });
  };

  const handleReport = async () => {
    if (!reportingId || !reportReason) return;
    await reportComment.mutateAsync({ commentId: reportingId, reason: reportReason, details: reportDetails || undefined });
    setReportingId(null);
    setReportReason('');
    setReportDetails('');
  };

  return (
    <div className="mt-4 rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/80 via-white to-purple-50/40 dark:from-indigo-500/10 dark:via-slate-800 dark:to-purple-500/10 overflow-hidden shadow-sm">
      {/* ── Header with toggle ──────────────────────────────────────────── */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3.5 active:bg-indigo-50 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-sm">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-white">
              <path fillRule="evenodd" d="M10 2c-2.236 0-4.43.18-6.57.524C1.993 2.755 1 4.014 1 5.426v5.148c0 1.413.993 2.67 2.43 2.902 1.168.188 2.352.327 3.55.414.28.02.521.18.642.413l1.713 3.293a.75.75 0 001.33 0l1.713-3.293c.121-.233.362-.393.642-.413 1.198-.087 2.382-.226 3.55-.414 1.437-.232 2.43-1.49 2.43-2.902V5.426c0-1.413-.993-2.67-2.43-2.902A41.803 41.803 0 0010 2z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="text-left">
            <span className="text-sm font-bold text-slate-800">Student Discussion</span>
            {totalCount > 0 && (
              <span className="ml-2 text-[11px] font-bold text-white bg-indigo-500 px-1.5 py-0.5 rounded-full">{totalCount}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {!expanded && totalCount === 0 && <span className="text-[11px] text-indigo-400 font-medium">Be the first</span>}
          {!expanded && totalCount > 0 && <span className="text-[11px] text-indigo-400 font-medium">Join in</span>}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
            className={`w-5 h-5 text-indigo-400 transition-transform ${expanded ? 'rotate-180' : ''}`}>
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </div>
      </button>

      {/* ── Top comments preview (visible when collapsed + comments exist) ── */}
      {!expanded && topPreview.length > 0 && (
        <div className="px-4 pb-3 space-y-1.5 border-t border-indigo-100">
          {topPreview.map(c => (
            <div key={c.id} className="flex items-start gap-2.5 bg-white/70 rounded-xl px-3 py-2 border border-indigo-100/60 first:mt-3">
              <div className="flex flex-col items-center shrink-0 pt-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor"
                  className={`w-3.5 h-3.5 ${c.score > 0 ? 'text-indigo-500' : 'text-slate-300'}`}>
                  <path fillRule="evenodd" d="M11.78 9.78a.75.75 0 0 1-1.06 0L8 7.06 5.28 9.78a.75.75 0 0 1-1.06-1.06l3.25-3.25a.75.75 0 0 1 1.06 0l3.25 3.25a.75.75 0 0 1 0 1.06Z" clipRule="evenodd" />
                </svg>
                <span className={`text-[11px] font-bold ${c.score > 0 ? 'text-indigo-500' : 'text-slate-400'}`}>{c.score}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] text-slate-700 line-clamp-2 leading-snug">{c.body}</p>
                <span className="text-[10px] text-slate-400 mt-0.5 block">{c.author.displayName}</span>
              </div>
            </div>
          ))}
          {totalCount > topPreview.length && (
            <button onClick={() => setExpanded(true)} className="w-full text-center text-xs text-indigo-500 font-semibold py-1 hover:text-indigo-700 transition-colors">
              View all {totalCount} comments
            </button>
          )}
        </div>
      )}

      {/* ── Expanded discussion ─────────────────────────────────────────── */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-indigo-100">
          {/* Post input */}
          {canPost ? (
            <div className="flex gap-2 mt-3">
              <input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePost(); }}}
                placeholder="Share your thoughts on this question..."
                maxLength={2000}
                className="flex-1 border-2 border-indigo-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 placeholder-slate-400 transition-all"
              />
              <button
                onClick={handlePost}
                disabled={!newComment.trim() || postComment.isPending}
                className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-4 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40 active:scale-95 transition-all shadow-sm shrink-0"
              >
                Post
              </button>
            </div>
          ) : (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 mt-3">
              <p className="text-xs text-indigo-700 font-medium">Upgrade to Plus or Pro to post comments, vote, and see all discussions.</p>
              <p className="text-[11px] text-indigo-500 mt-0.5">Free users can preview the top comments.</p>
            </div>
          )}

          {/* Comments list */}
          {isLoading ? (
            <div className="text-center text-slate-400 text-sm py-4">Loading...</div>
          ) : comments.length === 0 ? (
            <div className="text-center py-5">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-indigo-400">
                  <path fillRule="evenodd" d="M10 2c-2.236 0-4.43.18-6.57.524C1.993 2.755 1 4.014 1 5.426v5.148c0 1.413.993 2.67 2.43 2.902 1.168.188 2.352.327 3.55.414.28.02.521.18.642.413l1.713 3.293a.75.75 0 001.33 0l1.713-3.293c.121-.233.362-.393.642-.413 1.198-.087 2.382-.226 3.55-.414 1.437-.232 2.43-1.49 2.43-2.902V5.426c0-1.413-.993-2.67-2.43-2.902A41.803 41.803 0 0010 2z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-sm font-medium text-slate-600">No comments yet</p>
              <p className="text-xs text-slate-400 mt-0.5">Share a tip, ask a question, or explain your reasoning</p>
            </div>
          ) : (
            <div className="space-y-2">
              {comments.map(comment => (
                <CommentCard
                  key={comment.id}
                  comment={comment}
                  animating={animatingVotes[comment.id]}
                  onVote={canVote ? (value) => handleVote(comment.id, value) : undefined}
                  onDelete={() => deleteComment.mutate(comment.id)}
                  onReport={() => setReportingId(comment.id)}
                />
              ))}
              {!canSeeAll && comments.length > 0 && (
                <div className="text-center text-xs text-indigo-400 font-medium py-2">Upgrade to Plus or Pro to see all comments and vote.</div>
              )}
            </div>
          )}

          {/* Report modal */}
          {reportingId && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/40" onClick={() => setReportingId(null)} />
              <div className="relative bg-white rounded-2xl p-5 w-full max-w-sm mx-4 space-y-3">
                <h3 className="font-semibold text-squishy-dark">Report Comment</h3>
                <div className="space-y-2">
                  {['Incorrect information', 'Spam', 'Inappropriate', 'Off-topic', 'Other'].map(r => (
                    <button
                      key={r}
                      onClick={() => setReportReason(r)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm border transition-colors ${
                        reportReason === r ? 'border-squishy-primary bg-teal-50 text-squishy-primary' : 'border-slate-200 text-slate-600'
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
                <textarea
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  placeholder="Additional details (optional)"
                  rows={2}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-squishy-primary"
                />
                <div className="flex gap-2">
                  <button onClick={() => setReportingId(null)} className="flex-1 py-2 border rounded-lg text-sm text-slate-600">Cancel</button>
                  <button
                    onClick={handleReport}
                    disabled={!reportReason || reportComment.isPending}
                    className="flex-1 py-2 bg-red-500 text-white rounded-lg text-sm font-medium disabled:opacity-40"
                  >
                    {reportComment.isPending ? 'Sending...' : 'Submit Report'}
                  </button>
                </div>
                {reportComment.isSuccess && <p className="text-xs text-green-600 text-center">Report submitted. Thank you!</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CommentCard({ comment, animating, onVote, onDelete, onReport }: {
  comment: QuestionComment;
  animating?: 'up' | 'down';
  onVote?: (value: number) => void;
  onDelete: () => void;
  onReport: () => void;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const score = comment.score;

  const timeAgo = (() => {
    const diff = Date.now() - new Date(comment.createdAt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  })();

  return (
    <div className={`relative bg-slate-50 rounded-xl p-3 transition-all duration-500 ${
      animating === 'up' ? 'animate-bounce-subtle' : animating === 'down' ? 'opacity-80' : ''
    }`} style={{ order: -score }}>
      <div className="flex items-start gap-2">
        {/* Vote buttons */}
        <div className="flex flex-col items-center gap-0.5 shrink-0 pt-0.5">
          <button
            onClick={() => onVote?.(comment.myVote === 1 ? 0 : 1)}
            disabled={!onVote}
            className={`p-0.5 rounded transition-all active:scale-110 ${
              comment.myVote === 1 ? 'text-squishy-primary scale-110' : 'text-slate-300 hover:text-slate-500'
            } disabled:hover:text-slate-300 disabled:cursor-not-allowed`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06-.02L10 9.832l-3.71 3.938a.75.75 0 01-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z" clipRule="evenodd" />
            </svg>
          </button>
          <span className={`text-xs font-bold min-w-[16px] text-center transition-all ${
            score > 0 ? 'text-squishy-primary' : score < 0 ? 'text-red-400' : 'text-slate-400'
          } ${animating ? 'scale-125' : ''}`}>
            {score}
          </span>
          <button
            onClick={() => onVote?.(comment.myVote === -1 ? 0 : -1)}
            disabled={!onVote}
            className={`p-0.5 rounded transition-all active:scale-110 ${
              comment.myVote === -1 ? 'text-red-400 scale-110' : 'text-slate-300 hover:text-slate-500'
            } disabled:hover:text-slate-300 disabled:cursor-not-allowed`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Comment body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-5 h-5 rounded-full bg-slate-300 flex items-center justify-center text-[9px] font-bold text-white shrink-0 overflow-hidden">
              {comment.author.avatarUrl ? (
                <img src={comment.author.avatarUrl} alt="" className="w-full h-full object-cover" />
              ) : comment.author.displayName[0]}
            </div>
            <span className="text-xs font-medium text-slate-700 truncate">{comment.author.displayName}</span>
            <span className="text-[10px] text-slate-400">{timeAgo}</span>
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{comment.body}</p>
        </div>

        {/* Menu */}
        <div className="relative shrink-0">
          <button onClick={() => setShowMenu(!showMenu)} className="p-1 text-slate-300 hover:text-slate-500 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path d="M10 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM10 8.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM11.5 15.5a1.5 1.5 0 10-3 0 1.5 1.5 0 003 0z" />
            </svg>
          </button>
          {showMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-6 z-20 bg-white border rounded-lg shadow-lg py-1 min-w-[120px]">
                {comment.isOwn ? (
                  <button
                    onClick={() => { setShowMenu(false); onDelete(); }}
                    className="w-full text-left px-3 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"
                  >Delete</button>
                ) : (
                  <button
                    onClick={() => { setShowMenu(false); onReport(); }}
                    className="w-full text-left px-3 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"
                  >Report</button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FlagButton({ flagged, onToggle, questionId }: { flagged: boolean; onToggle: () => void; questionId: string }) {
  const [showReport, setShowReport] = useState(false);
  return (
    <div className="absolute top-4 right-4 flex items-center gap-1 z-10">
      <ShareButton questionId={questionId} />
      <button
        onClick={(e) => { e.stopPropagation(); setShowReport(true); }}
        className="p-1.5 rounded-lg transition-colors hover:bg-red-50"
        title="Report a problem"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
          fill={flagged ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth={flagged ? 0 : 1.5}
          className={`w-5 h-5 transition-colors ${flagged ? 'text-red-500' : 'text-slate-300 hover:text-red-400'}`}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5" />
        </svg>
      </button>
      {showReport && (
        <ReportQuestionModal
          questionId={questionId}
          onClose={() => setShowReport(false)}
          onReported={() => { if (!flagged) onToggle(); setShowReport(false); }}
        />
      )}
    </div>
  );
}

function ReportQuestionModal({ questionId, onClose, onReported }: { questionId: string; onClose: () => void; onReported: () => void }) {
  const { data: me } = useMe();
  const report = useReportQuestion();
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const isPro = me?.user?.subscriptionTier === 'pro' || me?.user?.role === 'admin' || me?.user?.role === 'superadmin';

  const reasons = [
    { value: 'incorrect_answer', label: 'Question or answer choice is incorrect' },
    { value: 'incorrect_explanation', label: 'Explanation is incorrect' },
    ...(isPro ? [{ value: 'pro_explanation_insufficient', label: 'Pro explanation is not thorough enough' }] : []),
    { value: 'other', label: 'Other' },
  ];

  const handleSubmit = async () => {
    if (!reason) return;
    try {
      await report.mutateAsync({ questionId, reason, details: details.trim() || undefined });
      setSubmitted(true);
      setTimeout(onReported, 1200);
    } catch { /* silent */ }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-md p-5 animate-slide-up max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {submitted ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="font-semibold text-squishy-dark">Report submitted</p>
            <p className="text-sm text-slate-500 mt-1">Thank you for helping improve our content.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-squishy-dark">Report a Problem</h3>
                <p className="text-xs text-slate-500">Help us keep content accurate</p>
              </div>
              <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
            </div>

            <div className="space-y-2 mb-4">
              {reasons.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setReason(r.value)}
                  className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-colors ${
                    reason === r.value
                      ? 'border-red-400 bg-red-50 text-red-700 font-medium'
                      : 'border-slate-200 hover:border-red-200 hover:bg-red-50/50 dark:hover:bg-red-500/15 text-slate-700'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Add details (optional)"
              rows={3}
              maxLength={2000}
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-300 mb-4"
            />

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!reason || report.isPending}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold disabled:opacity-50 hover:bg-red-600 transition-colors"
              >
                {report.isPending ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ShareButton({ questionId }: { questionId: string }) {
  const createShare = useCreateShare();
  const [copied, setCopied] = useState(false);

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const data = await createShare.mutateAsync({ question_id: questionId });
      const url = `${window.location.origin}/q/${data.share_id}`;

      if (navigator.share) {
        await navigator.share({ title: 'Can you answer this?', url }).catch(() => {});
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      console.error('[share] failed', err);
    }
  };

  return (
    <button
      onClick={handleShare}
      disabled={createShare.isPending}
      className="p-1.5 rounded-lg transition-colors hover:bg-slate-100 relative"
      title="Share this question"
    >
      {createShare.isPending ? (
        <div className="w-5 h-5 border-2 border-squishy-primary border-t-transparent rounded-full animate-spin" />
      ) : copied ? (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-emerald-500">
          <path fillRule="evenodd" d="M19.916 4.626a.75.75 0 01.208 1.04l-9 13.5a.75.75 0 01-1.154.114l-6-6a.75.75 0 011.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 011.04-.208z" clipRule="evenodd" />
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-5 h-5 text-squishy-primary">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
        </svg>
      )}
    </button>
  );
}

function SessionCompleteScreen({ sessionId, startedAt, totalQuestions, sessionResults, correctCount, answeredCount, xpEarned, backPath, completeSession, navigate }: {
  sessionId: string | null;
  startedAt: string | null;
  totalQuestions: number;
  sessionResults: Record<string, { correct: boolean; difficulty: number; topic: string }>;
  correctCount: number;
  answeredCount: number;
  xpEarned: number;
  backPath: string;
  completeSession: ReturnType<typeof useCompleteSession>;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [completed, setCompleted] = useState(false);

  // Compute time taken
  const timeTaken = useMemo(() => {
    if (!startedAt) return '';
    const ms = Date.now() - new Date(startedAt).getTime();
    const totalSeconds = Math.floor(ms / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    if (mins >= 60) {
      const hrs = Math.floor(mins / 60);
      const remainMins = mins % 60;
      return `${hrs}h ${remainMins}m`;
    }
    return `${mins}m ${secs}s`;
  }, [startedAt]);

  const pct = answeredCount > 0 ? Math.round((correctCount / answeredCount) * 100) : 0;

  // Difficulty breakdown
  const difficultyStats = useMemo(() => {
    const results = Object.values(sessionResults);
    const easy = results.filter(r => r.difficulty <= 2);
    const medium = results.filter(r => r.difficulty === 3);
    const hard = results.filter(r => r.difficulty >= 4);
    const pctOf = (arr: typeof results) => arr.length > 0 ? Math.round((arr.filter(r => r.correct).length / arr.length) * 100) : -1;
    return {
      easy: { total: easy.length, pct: pctOf(easy) },
      medium: { total: medium.length, pct: pctOf(medium) },
      hard: { total: hard.length, pct: pctOf(hard) },
    };
  }, [sessionResults]);

  // Topics with wrong answers
  const wrongTopics = useMemo(() => {
    const topics = new Set<string>();
    for (const r of Object.values(sessionResults)) {
      if (!r.correct && r.topic !== 'Unknown') topics.add(r.topic);
    }
    return Array.from(topics);
  }, [sessionResults]);

  const [perfectResult, setPerfectResult] = useState<{ perfectSession: boolean; bonusXp: number; badgesEarned: any[] } | null>(null);
  const xp = useXp();

  // Celebration polish (visual/feedback only): one success haptic on arrival,
  // then let the stat tiles count up from zero.
  const [statsReady, setStatsReady] = useState(false);
  useEffect(() => {
    haptic.success();
    const timer = setTimeout(() => setStatsReady(true), 350);
    return () => clearTimeout(timer);
  }, []);

  const handleBackToDashboard = async () => {
    if (sessionId && !completed) {
      setCompleted(true);
      const result = await completeSession.mutateAsync(sessionId);
      if ((result as any)?.perfectSession) {
        setPerfectResult(result as any);
        xp.pushDelta((result as any).bonusXp);
      }
    }
    navigate(backPath);
  };

  return (
    <div className="relative space-y-4">
      {/* One tasteful confetti pass for the true completion moment */}
      <Confetti count={36} duration={2.2} />

      <FadeIn>
        <div className="bg-white rounded-3xl shadow-card border border-slate-100 p-6 text-center relative overflow-hidden">
          <motion.div
            initial={{ scale: 0, rotate: -12 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ ...springs.bouncy, delay: 0.15 }}
            className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-squishy-teal to-squishy-tealDark flex items-center justify-center shadow-pop"
          >
            {pct >= 80 ? (
              <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path fillRule="evenodd" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.563.563 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M5 13l4 4L19 7" />
              </svg>
            )}
          </motion.div>
          <h2 className="font-display text-2xl font-bold text-squishy-dark mb-1">
            {pct >= 80 ? 'Excellent work' : pct >= 50 ? 'Solid session' : 'Session complete'}
          </h2>
          <p className="text-sm text-slate-500">{timeTaken ? `${timeTaken} spent studying` : 'Every question sharpens your judgment'}</p>
        </div>
      </FadeIn>

      {/* Stats grid */}
      <Stagger className="grid grid-cols-3 gap-3">
        <Stagger.Item className="bg-white rounded-3xl shadow-card border border-slate-100 p-4 text-center">
          <CountUp value={statsReady ? answeredCount : 0} className="text-2xl font-display font-bold text-squishy-dark" />
          <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide mt-0.5">Answered</div>
        </Stagger.Item>
        <Stagger.Item className="bg-white rounded-3xl shadow-card border border-slate-100 p-4 text-center">
          <CountUp
            value={statsReady ? pct : 0}
            format={(n) => `${n}%`}
            className={`text-2xl font-display font-bold ${pct >= 70 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-500' : 'text-red-500'}`}
          />
          <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide mt-0.5">Correct</div>
        </Stagger.Item>
        <Stagger.Item className="bg-white rounded-3xl shadow-card border border-slate-100 p-4 text-center">
          <CountUp
            value={statsReady ? xpEarned : 0}
            format={(n) => (n >= 0 ? `+${n}` : `${n}`)}
            className="text-2xl font-display font-bold text-squishy-tealDark"
          />
          <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide mt-0.5">XP Earned</div>
        </Stagger.Item>
      </Stagger>

      {/* Perfect session bonus */}
      {perfectResult?.perfectSession && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-500/10 dark:to-orange-500/10 border-2 border-amber-200 rounded-3xl px-4 py-3 text-center animate-pop-in">
          <p className="text-amber-700 font-display font-bold">Perfect Session! +30 XP Bonus</p>
          {perfectResult.badgesEarned?.length > 0 && (
            <p className="text-amber-600 text-sm mt-1">
              Badge earned: {perfectResult.badgesEarned.map((b: any) => b.name).join(', ')}
            </p>
          )}
        </div>
      )}

      {/* Correct / Wrong breakdown */}
      <FadeIn delay={0.15}>
        <div className="bg-white rounded-3xl shadow-card border border-slate-100 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-slate-700">Results</span>
            <span className="text-xs text-slate-400">{correctCount} correct, {answeredCount - correctCount} wrong</span>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden flex">
            {correctCount > 0 && (
              <motion.div
                className="bg-emerald-500 h-full rounded-l-full"
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ ...springs.soft, delay: 0.35 }}
              />
            )}
            {answeredCount - correctCount > 0 && (
              <motion.div
                className="bg-red-400 h-full rounded-r-full"
                initial={{ width: 0 }}
                animate={{ width: `${100 - pct}%` }}
                transition={{ ...springs.soft, delay: 0.35 }}
              />
            )}
          </div>
        </div>
      </FadeIn>

      {/* Difficulty breakdown */}
      {Object.values(sessionResults).length > 0 && (
        <FadeIn delay={0.22}>
          <div className="bg-white rounded-3xl shadow-card border border-slate-100 p-4">
            <div className="text-sm font-semibold text-slate-700 mb-3">By Difficulty</div>
            <div className="space-y-2">
              {[
                { label: 'Easy', data: difficultyStats.easy, color: 'green' },
                { label: 'Medium', data: difficultyStats.medium, color: 'amber' },
                { label: 'Hard', data: difficultyStats.hard, color: 'red' },
              ].filter(d => d.data.total > 0).map((d, di) => (
                <div key={d.label} className="flex items-center gap-3">
                  <span className="text-xs text-slate-600 w-16">{d.label}</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <motion.div
                      className={`h-full rounded-full ${d.color === 'green' ? 'bg-emerald-500' : d.color === 'amber' ? 'bg-amber-500' : 'bg-red-500'}`}
                      initial={{ width: 0 }}
                      animate={{ width: `${d.data.pct}%` }}
                      transition={{ ...springs.soft, delay: 0.4 + di * 0.08 }}
                    />
                  </div>
                  <span className="text-xs text-slate-500 w-12 text-right">{d.data.pct}%</span>
                  <span className="text-[10px] text-slate-400 w-6 text-right">({d.data.total})</span>
                </div>
              ))}
            </div>
          </div>
        </FadeIn>
      )}

      {/* Topics to review */}
      {wrongTopics.length > 0 && (
        <FadeIn delay={0.28}>
          <div className="bg-white rounded-3xl shadow-card border border-slate-100 p-4">
            <div className="text-sm font-semibold text-slate-700 mb-2">Topics to Review</div>
            <div className="flex flex-wrap gap-1.5">
              {wrongTopics.map(topic => (
                <span key={topic} className="text-xs bg-red-50 text-red-600 px-2.5 py-1 rounded-full border border-red-200">
                  {topic}
                </span>
              ))}
            </div>
          </div>
        </FadeIn>
      )}

      {/* Back to dashboard */}
      <Pressable
        haptic="medium"
        onClick={handleBackToDashboard}
        disabled={completeSession.isPending}
        className="w-full bg-gradient-to-r from-squishy-teal to-squishy-tealDark text-white py-4 rounded-2xl font-bold text-base shadow-pop disabled:opacity-50 transition-opacity"
      >
        {completeSession.isPending ? 'Saving...' : 'Back to Dashboard'}
      </Pressable>
    </div>
  );
}

function ExplanationBlock({ payload, result, questionStats }: { payload: any; result: any; questionStats?: any }) {
  const hasExplanation = payload.explanation && payload.explanation.trim().length > 0;
  const stats = result?.questionStats ?? questionStats;
  const percent = typeof stats?.firstAttemptCorrectPercent === 'number'
    ? `${stats.firstAttemptCorrectPercent}% got this right first try`
    : null;
  const difficulty = typeof stats?.difficulty === 'number' && typeof stats?.difficultyMax === 'number'
    ? `Difficulty ${stats.difficulty}/${stats.difficultyMax}`
    : null;
  return (
    <div className="mt-4 p-4 bg-slate-50 rounded-2xl text-sm border border-slate-200">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="font-semibold text-slate-700">Explanation</span>
        {percent && <span className="text-[11px] font-medium text-slate-500 bg-white border border-slate-200 px-2 py-0.5 rounded-full">{percent}</span>}
        {difficulty && <span className="text-[11px] font-medium text-slate-500 bg-white border border-slate-200 px-2 py-0.5 rounded-full">{difficulty}</span>}
      </div>
      {hasExplanation ? (
        <div className="text-slate-600 leading-relaxed">{formatExplanation(payload.explanation, payload)}</div>
      ) : (
        <p className="text-slate-400 italic">No explanation available for this question.</p>
      )}
      {payload.mcat_section && (
        <div className="mt-2 flex gap-1.5 flex-wrap">
          <span className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium uppercase">{payload.mcat_section}</span>
          {payload.passage_type && <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full capitalize">{payload.passage_type}</span>}
          {payload.cars_category && <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full capitalize">{payload.cars_category.replace(/_/g, ' ')}</span>}
        </div>
      )}
      {payload.source && <div className="mt-3 text-xs text-slate-400">{payload.source}</div>}
      {payload.citation_urls?.length > 0 && (
        <div className="mt-1 space-y-0.5">
          {payload.citation_urls.map((url: string, i: number) => (
            <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block text-xs text-squishy-primary hover:underline truncate">
              {url}
            </a>
          ))}
        </div>
      )}
      {result && (
        /* Same three-state ladder as the banner. "No XP change" must stay
           reachable ONLY for a real numeric 0 (the server's negative-XP clamp);
           showing it while the attempt is still in flight tells the student they
           earned nothing and then silently changes its mind. */
        result.saveFailed ? (
          <div className="mt-3 text-xs font-semibold text-amber-600">Not saved yet, will retry</div>
        ) : typeof result.xpEarned !== 'number' ? (
          <div className="mt-3 text-xs font-semibold text-slate-400">Saving…</div>
        ) : (
          <div className={`mt-3 text-xs font-semibold ${result.xpEarned > 0 ? 'text-emerald-600' : result.xpEarned < 0 ? 'text-red-500' : 'text-slate-400'}`}>
            {result.xpEarned > 0 ? `+${result.xpEarned} XP` : result.xpEarned < 0 ? `${result.xpEarned} XP` : 'No XP change'}
          </div>
        )
      )}
    </div>
  );
}
